import {
  ETIQUETA_SITUACION_DE_CUMPLIMIENTO,
  ETIQUETA_SITUACION_DE_CUMPLIMIENTO_EN,
  type Semaforo,
  type SituacionDeCumplimiento,
} from '@cnc/contracts';
import {et, tr} from '../i18n/idioma';

// Como se ve cada situacion de Cumplimiento (decision 0045), con los colores de
// la paleta para que funcionen en el tema claro y en el oscuro.
export const ESTILO_DE_SITUACION: Record<SituacionDeCumplimiento, {clase: string; icono: string}> = {
  vencido: {clase: 'border-alerta/50 bg-red-50 text-alerta', icono: '✕'},
  falta: {clase: 'border-alerta/40 bg-white text-alerta', icono: '!'},
  por_vencer: {clase: 'border-amber-300 bg-amber-50 text-amber-900', icono: '⏳'},
  en_revision: {clase: 'border-linea bg-papel text-tinta', icono: '◔'},
  al_dia: {clase: 'border-verde/30 bg-verde-claro text-verde-oscuro', icono: '✓'},
  no_aplica: {clase: 'border-linea bg-white text-apagado', icono: '—'},
  sin_documento: {clase: 'border-dashed border-linea bg-white text-apagado', icono: '·'},
};

export const COLOR_DE_SEMAFORO: Record<Semaforo, string> = {
  rojo: 'bg-alerta',
  amarillo: 'bg-dorado',
  gris: 'bg-apagado',
  verde: 'bg-verde',
  inactivo: 'border border-apagado bg-white',
};

// Situacion de un archivo suelto (en el Archivo): vencido, por vencer o vigente
// segun su fecha; los que no avisan (historial, formatos, sin clasificar) no
// llevan color.
export function situacionDeArchivo(
  documento: {avisa: boolean; venceEl: string | null},
  hoy: string,
  diasDeAviso = 60,
): SituacionDeCumplimiento | null {
  if (!documento.avisa || !documento.venceEl) return null;
  const dias = Math.round((Date.parse(documento.venceEl) - Date.parse(hoy)) / 86_400_000);
  return dias < 0 ? 'vencido' : dias <= diasDeAviso ? 'por_vencer' : 'al_dia';
}

export const tamanoLegible = (bytes: number | null) =>
  bytes === null
    ? ''
    : bytes < 1024 * 1024
      ? `${Math.max(1, Math.round(bytes / 1024))} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

export const etiquetaDeSituacion = (s: SituacionDeCumplimiento) =>
  et(ETIQUETA_SITUACION_DE_CUMPLIMIENTO, ETIQUETA_SITUACION_DE_CUMPLIMIENTO_EN)[s];

export function textoDeDias(dias: number | null): string {
  if (dias === null) return '';
  if (dias < 0) return tr(`venció hace ${-dias} días`, `expired ${-dias} days ago`);
  if (dias === 0) return tr('vence hoy', 'expires today');
  if (dias === 1) return tr('vence mañana', 'expires tomorrow');
  return tr(`vence en ${dias} días`, `expires in ${dias} days`);
}

// Filtros del Archivo (los guarda la pagina para abrirlo desde otras pestanas).
export type FiltrosDeArchivo = {
  carpeta: string;
  buscar: string;
  categoria: string;
  tipo: string;
  vinculo: string;
  vencimiento: '' | 'vencidos' | 'por_vencer';
  pagina: number;
  porPagina: number;
};
export const ARCHIVO_INICIAL: FiltrosDeArchivo = {
  carpeta: '',
  buscar: '',
  categoria: '',
  tipo: '',
  vinculo: '',
  vencimiento: '',
  pagina: 1,
  porPagina: 100,
};

// Nombre de un tipo de documento para un vinculo: el equivalente del exterior
// si la empresa es del exterior (Tax ID en vez de RUT...).
export const nombreDelTipo = (
  tipo: {nombre: string; nombreInternacional: string | null},
  vinculo: {internacional: boolean},
) => (vinculo.internacional && tipo.nombreInternacional) || tipo.nombre;
